import React from "react";
import { Route } from "react-router-dom";
import MainLayout from "../../components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

// Lazy load route components
const EmailTemplatesList = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailTemplateManagement/components/EmailTemplateList/EmailTemplatesList"
    ),
);
const CreateEmailTemplate = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailTemplateManagement/components/CreateEmailTemplate/CreateEmailTemplate"
    ),
);
const EditEmailTemplate = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailTemplateManagement/components/EditEmailTemplate/EditEmailTemplate"
    ),
);
const EmailCampaignList = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailCampaignManagement/components/EmailCampaignList/EmailCampaignList"
    ),
);
const CreateEmailCampaign = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailCampaignManagement/components/CreateEmailCampaign/CreateEmailCampaign"
    ),
);
const EmailTemplateCategoryList = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailTemplateCategoryManagement/components/EmailTemplateCategoryList/EmailTemplateCategoryList"
    ),
);
const EmailEventLogList = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailEventLogsManagement/component/EmailEventLogList/EmailEventLogList"
    ),
);
const UpdateEmailCampaign = React.lazy(
  () =>
    import(
      "@/modules/emailManagement/features/EmailCampaignManagement/components/UpdateEmailCampaign/UpdateEmailCampaign"
    ),
);
const EmailAudienceBuilderPage = React.lazy(() =>
  import("@/modules/emailManagement/features/EmailAudienceBuilder").then(
    (module) => ({ default: module.EmailAudienceBuilderPage }),
  ),
);

// Create stable component reference
const EmailManagementMainLayout = () => (
  <MainLayout section="email-management" />
);

/**
 * EmailManagementRoutes
 *
 * Wraps the /email-management section in the Auth0-based authentication guard, then
 * protects individual routes with PermissionGuard based on the required
 * permissions from your navigation config.
 */
const EmailManagementRoutes = (
  <Route
    element={<AuthenticationGuard component={EmailManagementMainLayout} />}
    path="/email-management"
  >
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EmailTemplatesList />
        </PermissionGuard>
      }
      path="email-templates"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <CreateEmailTemplate />
        </PermissionGuard>
      }
      path="email-template/new"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EditEmailTemplate />
        </PermissionGuard>
      }
      path="email-templates/:emailTemplateId/edit"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EmailTemplateCategoryList />
        </PermissionGuard>
      }
      path="email-template-categories"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EmailCampaignList />
        </PermissionGuard>
      }
      path="email-campaigns"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <CreateEmailCampaign />
        </PermissionGuard>
      }
      path="email-campaign/new"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EmailEventLogList />
        </PermissionGuard>
      }
      path="email/activity"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <UpdateEmailCampaign />
        </PermissionGuard>
      }
      path="email-campaign/:emailCampaignId/edit"
    />

    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <EmailAudienceBuilderPage />
        </PermissionGuard>
      }
      path="audience/new"
    />
  </Route>
);

export default EmailManagementRoutes;
